import { ReactNode } from 'react';
import { Label } from '@givebutter/primitives/label';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { twMerge } from 'tailwind-merge';

const PaymentMethodsRoot = (props: { children: ReactNode }) => {
    const { children } = props;

    return (
        <div className="bg-gray-25 overflow-clip rounded-lg border border-gray-200">{children}</div>
    );
};

const PaymentMethodsOption = (props: { children: ReactNode }) => {
    const { children } = props;

    return (
        <Collapsible.Root className="flex flex-col border-b border-gray-200 last-of-type:border-b-0">
            {children}
        </Collapsible.Root>
    );
};

const PaymentMethodsLabel = (props: { children: ReactNode; className?: string }) => {
    const { children, className = '' } = props;

    return (
        <div
            className={twMerge(
                'flex min-h-8 w-full flex-row items-center justify-between gap-4 pr-4 hover:bg-gray-100',
                className,
            )}
        >
            {children}
        </div>
    );
};

const PaymentMethodsTrigger = (props: { children: ReactNode }) => {
    const { children } = props;

    return (
        <Collapsible.Trigger className="group flex w-full cursor-pointer flex-row items-center gap-3 px-4 py-3">
            <FontAwesomeIcon
                icon={faChevronRight}
                className="group-radix-state-open:rotate-90 text-sm text-gray-400 transition-transform"
            />
            <Label className="w-full cursor-pointer text-base">
                <span className="flex items-center gap-2">{children}</span>
            </Label>
        </Collapsible.Trigger>
    );
};

const PaymentMethodsContent = (props: { children: ReactNode }) => {
    const { children } = props;

    return (
        <Collapsible.Content>
            <div className="flex flex-col gap-4 px-6 py-4">{children}</div>
        </Collapsible.Content>
    );
};

const PaymentMethodsDescriptionContainer = (props: { children: ReactNode }) => {
    const { children } = props;

    return <div className="flex gap-6">{children}</div>;
};

const PaymentMethodsDescription = (props: { children: ReactNode }) => {
    const { children } = props;

    return <div className="w-full text-sm">{children}</div>;
};

const PaymentMethodsDescriptionSeparator = () => <div className="w-px bg-gray-100" />;

const PaymentMethodsDetailsContainer = (props: { children: ReactNode }) => {
    const { children } = props;

    return <div className="flex w-full flex-col gap-4">{children}</div>;
};

const PaymentMethodsProcessingFee = (props: { children: ReactNode }) => {
    const { children } = props;

    return (
        <div className="text-sm">
            <Label>Processing Fee</Label>
            <div>{children}</div>
        </div>
    );
};

const PaymentMethodsCompatibility = (props: { children: ReactNode }) => {
    const { children } = props;

    return (
        <div className="text-sm">
            <Label>Compatibility</Label>
            <div>{children}</div>
        </div>
    );
};

const PaymentMethodsActions = (props: { children: ReactNode }) => {
    const { children } = props;

    return <div className="flex justify-end gap-4">{children}</div>;
};

const Root = PaymentMethodsRoot;
const Option = PaymentMethodsOption;
const Trigger = PaymentMethodsTrigger;
const Content = PaymentMethodsContent;
const DescriptionContainer = PaymentMethodsDescriptionContainer;
const Description = PaymentMethodsDescription;
const DescriptionSeparator = PaymentMethodsDescriptionSeparator;
const DetailsContainer = PaymentMethodsDetailsContainer;
const ProcessingFee = PaymentMethodsProcessingFee;
const Compatibility = PaymentMethodsCompatibility;
const Actions = PaymentMethodsActions;

export {
    Root,
    Option,
    PaymentMethodsLabel as Label,
    Trigger,
    Content,
    DescriptionContainer,
    Description,
    DescriptionSeparator,
    DetailsContainer,
    ProcessingFee,
    Compatibility,
    Actions,
    //
    PaymentMethodsRoot,
    PaymentMethodsOption,
    PaymentMethodsTrigger,
    PaymentMethodsContent,
    PaymentMethodsDescriptionContainer,
    PaymentMethodsDescription,
    PaymentMethodsDescriptionSeparator,
    PaymentMethodsDetailsContainer,
    PaymentMethodsProcessingFee,
    PaymentMethodsCompatibility,
    PaymentMethodsActions,
};
